import VerificaTraduzioniOnline from "./VerificaTraduzioniOnline";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import apiUrl from "../Apiurl";
import { BlockSkeleton, TextSkeleton } from "../interfaccia/loading";
import stilePulsanti from "../interfaccia/stilePulsanti";
import TraduzioneMassiva from "./TraduzioneMassiva";
import "./TraduzioniPage.css";

// Filtri esatti (stagioneEq/brandEq) come nelle altre pagine Moda: le tendine
// evitano le corrispondenze parziali della ricerca testuale.
const INITIAL_SELECTION = { search: "", stagioneEq: "", brandEq: "", stockMode: "all" };

// Stessi codici stagione di ModaArticoliBrowser (YY1/YY2 dal 2020 all'anno
// prossimo), dalla piu' recente.
export function seasonOptions(now = new Date()) {
  const lastYear = (now.getFullYear() % 100) + 1;
  const options = [];
  for (let yy = lastYear; yy >= 20; yy -= 1) {
    const year = String(yy).padStart(2, "0");
    options.push(`${year}2`, `${year}1`);
  }
  return options;
}

function brandOptionsFrom(payload) {
  const list = Array.isArray(payload?.data) ? payload.data : [];
  return list
    .filter((item) => item && typeof item.code === "string" && item.code.trim())
    .map((item) => ({ code: item.code.trim(), description: item.description || item.code.trim() }));
}

function activeSelection(selection) {
  return Object.fromEntries(Object.entries(selection).filter(([, value]) => value !== ""));
}
const SITES = ["VIG", "BOT"];
const REASONS = {
  NON_APPLICABILE: "Destinazione disabilitata",
  ANNULLATO: "Annullato",
  DISATTIVO: "Disattivo",
  ATTIVO_NON_DEFINITO: "Stato attivo non definito",
  NON_PUBBLICARE: "Non pubblicare",
  SOLO_ITALIA: "Brand non venduto all'estero",
  ID_SITO_MANCANTE: "ID sito mancante",
};
const STATES = {
  MANCANTE: "Mancante", PARZIALE: "Parziale", PRONTO: "Pronto", DA_AGGIORNARE: "Da aggiornare",
  NON_VERIFICATO: "Non verificato", ASSENTE: "Assente", SEGNAPOSTO: "Segnaposto",
  PRESENTE: "Presente", ALLINEATO: "Allineato", INVIATO_NON_VERIFICATO: "Inviato, non verificato",
  ERRORE_LETTURA: "Errore di lettura",
};
const SITE_METRICS = {
  eleggibili: "Eleggibili", tradottiDomino: "Tradotti in Domino", online: "Online",
  nonVerificati: "Non verificati online", erroriLettura: "Errori di lettura", nonCalcolati: "Stato Domino non calcolato",
};
const LANGUAGE_METRICS = ["mancantiDomino", "daAggiornare", "mancantiOnline", "nonVerificati", "erroriLettura", "nonCalcolati"];

function requireCount(value) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error("Risposta di copertura incompleta: conteggi non validi.");
}

function validateCoverage(summary, articles) {
  if (!summary || !articles || !Array.isArray(articles.rows)) throw new Error("Risposta di copertura incompleta.");
  [summary.totaleSelezionati, summary.totaleEleggibili, articles.total].forEach(requireCount);
  if (summary.totaleSelezionati !== articles.total) {
    throw new Error("La selezione è cambiata durante la lettura. Aggiorna la copertura.");
  }
  SITES.forEach((site) => {
    const data = summary.siti?.[site];
    if (!data || !data.lingue || !Object.keys(data.lingue).length || !data.esclusi ||
        typeof data.bloccatoConfigurazione !== "boolean") throw new Error("Risposta di copertura incompleta.");
    Object.keys(SITE_METRICS).forEach((metric) => requireCount(data[metric]));
    Object.keys(REASONS).forEach((reason) => requireCount(data.esclusi[reason]));
    Object.values(data.lingue).forEach((language) => LANGUAGE_METRICS.forEach((metric) => requireCount(language[metric])));
    articles.rows.forEach((row) => {
      const articleSite = row.siti?.[site];
      if (!row.codVS || !articleSite?.lingue || typeof articleSite.eleggibile !== "boolean" ||
          typeof articleSite.tradottoDomino !== "boolean" || typeof articleSite.online !== "boolean") {
        throw new Error("Risposta articoli incompleta.");
      }
      Object.keys(data.lingue).forEach((language) => {
        const state = articleSite.lingue[language];
        if (!state || !(state.statoDomino === null || typeof state.statoDomino === "string") ||
            typeof state.statoOnline !== "string" || !Array.isArray(state.campiMancanti) || typeof state.copiaIt !== "boolean") {
          throw new Error("Risposta articoli incompleta.");
        }
      });
    });
  });
}

function SiteCard({ site, coverage }) {
  return <section className="traduzioni-card sfondoBiancoTema ba bordoColAlt br3 pa3" aria-label={`Copertura ${site}`}>
    <h2 className="mt0">{site}</h2>
    {coverage.bloccatoConfigurazione && <p className="ba bordoColAlt br2 pa2" role="note">Invio testi bloccato da configurazione.</p>}
    <dl className="traduzioni-metrics">
      {Object.entries(SITE_METRICS).map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{coverage[key].toLocaleString("it-IT")}</dd></div>)}
    </dl>
    <div className="traduzioni-table-scroll">
      <table className="traduzioni-language-table">
        <caption>Copertura per lingua sugli articoli eleggibili</caption>
        <thead><tr><th>Lingua</th><th>Mancanti in Domino</th><th>Da aggiornare</th><th>Mancanti online</th><th>Non verificati</th><th>Errori lettura</th><th>Non calcolati</th></tr></thead>
        <tbody>{Object.entries(coverage.lingue).map(([language, metrics]) => <tr key={language}>
          <th>{language.toUpperCase()}</th>{LANGUAGE_METRICS.map((metric) => <td key={metric}>{metrics[metric]}</td>)}
        </tr>)}</tbody>
      </table>
    </div>
    <div className="f6 mt3">Esclusi dalla copertura:
      <ul>{Object.entries(REASONS).map(([reason, label]) => <li key={reason}>{label}: {coverage.esclusi[reason]}</li>)}</ul>
    </div>
  </section>;
}

function ArticleSite({ data }) {
  if (!data.eleggibile) return <span className="traduzioni-badge ba bordoColAlt">{REASONS[data.motivo] || data.motivo}</span>;
  return <div>
    <div className="mb2"><span className="traduzioni-badge ba bordoColAlt">Eleggibile</span>
      {data.tradottoDomino && <span className="traduzioni-badge ba bordoColAlt">Tradotto in Domino</span>}
      {data.online && <span className="traduzioni-badge ba bordoColAlt">Online</span>}
    </div>
    {Object.entries(data.lingue).map(([language, state]) => <div key={language} className="traduzioni-language-state">
      <strong>{language.toUpperCase()}</strong>
      <span className="traduzioni-badge ba bordoColAlt" title={state.campiMancanti?.length ? `Campi mancanti: ${state.campiMancanti.join(", ")}` : undefined}>
        Domino: {state.statoDomino ? STATES[state.statoDomino] || state.statoDomino : "Non calcolato"}
      </span>
      <span className="traduzioni-badge ba bordoColAlt">Online: {STATES[state.statoOnline] || state.statoOnline}</span>
      {state.copiaIt && <span className="traduzioni-badge ba bordoColAlt">Copia italiano</span>}
    </div>)}
  </div>;
}

export default function TraduzioniPage({ utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const canRead = Boolean(utente?.permessiAPI?.["read:database"]);
  const [draft, setDraft] = useState(INITIAL_SELECTION);
  const [selection, setSelection] = useState(INITIAL_SELECTION);
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [brands, setBrands] = useState([]);
  const [brandsError, setBrandsError] = useState(false);
  const perPage = 25;
  const verificaCompletata = useCallback(() => setRefresh((value) => value + 1), []);

  useEffect(() => {
    if (!canRead) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${apiUrl}/moda/lookup/brand`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        if (!controller.signal.aborted) setBrands(brandOptionsFrom(response.data));
      } catch (err) {
        if (!controller.signal.aborted) setBrandsError(true);
      }
    })();
    return () => controller.abort();
  }, [canRead, getAccessTokenSilently]);

  useEffect(() => {
    if (!canRead) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    const load = async () => {
      let correctingPage = false;
      try {
        const token = await getAccessTokenSilently();
        if (!active) return;
        const options = { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal };
        const [summaryResponse, articlesResponse] = await Promise.all([
          axios.get(`${apiUrl}/prodotti/translate/copertura`, { ...options, params: activeSelection(selection) }),
          axios.get(`${apiUrl}/prodotti/translate/copertura/articoli`, { ...options, params: { ...activeSelection(selection), page, perPage } }),
        ]);
        if (!active) return;
        if (!summaryResponse.data?.success || !articlesResponse.data?.success) throw new Error("Copertura non disponibile.");
        const summary = summaryResponse.data.data;
        const articles = articlesResponse.data.data;
        validateCoverage(summary, articles);
        const lastPage = Math.max(1, Math.ceil(articles.total / perPage));
        if (page > lastPage) { correctingPage = true; setPage(lastPage); return; }
        setSnapshot({ summary, articles });
      } catch (err) {
        if (active && !controller.signal.aborted) {
          setSnapshot(null);
          setError(err.response?.data?.details?.join(" ") || err.response?.data?.error || err.message || "Errore durante la lettura della copertura.");
        }
      } finally {
        if (active && !correctingPage) setLoading(false);
      }
    };
    load();
    return () => { active = false; controller.abort(); };
  }, [canRead, getAccessTokenSilently, selection, page, refresh]);

  if (!canRead) return <div className="pa3" role="alert">Per consultare le traduzioni serve il permesso read:database.</div>;
  const submit = (event) => {
    event.preventDefault();
    setPage(1);
    setSelection(Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, value.trim()])));
  };
  const field = (key, label) => <label>{label}<input className={stilePulsanti.inputClassesNoMeasure}
    value={draft[key]} onChange={(event) => setDraft({ ...draft, [key]: event.target.value })} /></label>;
  return <div className="traduzioni-page pa3">
    <div className="traduzioni-heading"><h1>Traduzioni</h1>
      <button className={stilePulsanti.buttonClassesWhite} onClick={() => setRefresh((value) => value + 1)} disabled={loading}>Aggiorna copertura</button>
    </div>
    <p>Copertura dei testi VIG e BOT registrata in Domino. La presenza online è quella dell'ultima verifica disponibile.</p>
    <form className="traduzioni-filters" onSubmit={submit} aria-label="Filtri traduzioni">
      {field("search", "Ricerca")}
      <label>Stagione<select className={stilePulsanti.inputClassesNoMeasure} value={draft.stagioneEq} onChange={(event) => setDraft({ ...draft, stagioneEq: event.target.value })}>
        <option value="">Tutte</option>
        {seasonOptions().map((season) => <option key={season} value={season}>{season}</option>)}
      </select></label>
      <label>Brand<select className={stilePulsanti.inputClassesNoMeasure} value={draft.brandEq} onChange={(event) => setDraft({ ...draft, brandEq: event.target.value })}>
        <option value="">Tutti</option>
        {brands.map((brand) => <option key={brand.code} value={brand.code}>{brand.description} ({brand.code})</option>)}
      </select></label>
      {brandsError && <span className="f7" role="status">Elenco brand non disponibile.</span>}
      <label>Stock<select className={stilePulsanti.inputClassesNoMeasure} value={draft.stockMode} onChange={(event) => setDraft({ ...draft, stockMode: event.target.value })}>
        <option value="all">Tutti</option><option value="web">Con stock web</option><option value="moda">Con giacenza Moda</option>
      </select></label>
      <button className={stilePulsanti.buttonClassesWhite} type="submit">Applica filtri</button>
    </form>
    <VerificaTraduzioniOnline utente={utente} selection={activeSelection(selection)}
      eligibleCount={snapshot?.summary.totaleEleggibili} disabled={loading || Boolean(error) || !snapshot}
      onComplete={verificaCompletata} />
    {error && <p role="alert">{error}</p>}
    {loading ? <div aria-label="Lettura copertura" aria-busy="true">
      <TextSkeleton lines={2} /><div className="traduzioni-cards"><BlockSkeleton height={320} /><BlockSkeleton height={320} /></div>
      <BlockSkeleton height={320} className="mt3" />
    </div> : snapshot && <>
      <p aria-live="polite">Articoli selezionati: <strong>{snapshot.summary.totaleSelezionati}</strong> · Eleggibili su almeno un sito: <strong>{snapshot.summary.totaleEleggibili}</strong></p>
      <div className="traduzioni-cards">{SITES.map((site) => <SiteCard key={site} site={site} coverage={snapshot.summary.siti[site]} />)}</div>
      {utente?.permessiAPI?.["usage:googleapis"] && utente?.permessiAPI?.["update:database"] &&
        <TraduzioneMassiva selection={activeSelection(selection)} utente={utente} />}
      <p className="f6">Tradotti in Domino e online richiedono tutte le lingue target. Mancanti in Domino include gli stati Mancante e Parziale; mancanti online include Assente e Segnaposto. Stati non calcolati, non verificati ed errori di lettura sono separati. I conteggi delle due card possono sovrapporsi.</p>
      <div className="traduzioni-table-scroll mt3">
        <table className="traduzioni-articles"><caption>Articoli della selezione</caption>
          <thead><tr><th>Articolo</th><th>Brand / stagione</th><th>VIG</th><th>BOT</th></tr></thead>
          <tbody>{snapshot.articles.rows.map((row) => <tr key={row.codVS}>
            <th scope="row">{row.codVS}<div className="fw4 mt1">{row.nome}</div></th>
            <td>{row.brandName || row.brand || "—"}<div className="mt1">{row.stagione || "—"}</div></td>
            {SITES.map((site) => <td key={site}><ArticleSite data={row.siti[site]} /></td>)}
          </tr>)}</tbody>
        </table>
        {!snapshot.articles.rows.length && <p>Nessun articolo nella selezione.</p>}
      </div>
      <div className="traduzioni-pagination">
        <button className={stilePulsanti.buttonClassesWhite} disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Precedente</button>
        <span>Pagina {page} di {Math.max(1, Math.ceil(snapshot.articles.total / perPage))}</span>
        <button className={stilePulsanti.buttonClassesWhite} disabled={page * perPage >= snapshot.articles.total} onClick={() => setPage((value) => value + 1)}>Successiva</button>
      </div>
    </>}
  </div>;
}
